import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { AppShell } from "./components/layout/AppShell";

const LandingPage = lazy(() => import("./pages/public/LandingPage"));
const LoginPage = lazy(() => import("./pages/public/LoginPage"));
const RegisterPage = lazy(() => import("./pages/public/RegisterPage"));
const ForgotPasswordPage = lazy(
  () => import("./pages/public/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("./pages/public/ResetPasswordPage"),
);
const VerifyEmailPage = lazy(() => import("./pages/public/VerifyEmailPage"));
const AboutPage = lazy(() => import("./pages/public/AboutPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const HelpPage = lazy(() => import("./pages/public/HelpPage"));
const HostsPage = lazy(() => import("./pages/public/HostsPage"));
const CleanersPage = lazy(() => import("./pages/public/CleanersPage"));
const PrivacyPolicyPage = lazy(
  () => import("./pages/public/PrivacyPolicyPage"),
);
const TermsPage = lazy(() => import("./pages/public/TermsPage"));
const KycPolicyPage = lazy(() => import("./pages/public/KycPolicyPage"));
const SafetyPage = lazy(() => import("./pages/public/SafetyPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const KYCReview = lazy(() => import("./pages/admin/KYCReview"));
const SystemConfig = lazy(() => import("./pages/admin/SystemConfig"));
const DisputeResolution = lazy(() => import("./pages/admin/DisputeResolution"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminAudit = lazy(() => import("./pages/admin/AuditLog"));
const AdminAgents = lazy(() => import("./pages/admin/Agents"));
const AdminJobs = lazy(() => import("./pages/admin/Jobs"));
const AdminProperties = lazy(() => import("./pages/admin/Properties"));
const SetupAccountPage = lazy(() => import("./pages/public/SetupAccountPage"));
const HostDashboard = lazy(() => import("./pages/host/HostDashboard"));
const PropertiesPage = lazy(() => import("./pages/host/PropertiesPage"));
const PropertyFormPage = lazy(() => import("./pages/host/PropertyFormPage"));
const JobsPage = lazy(() => import("./pages/host/JobsPage"));
const PostJobPage = lazy(() => import("./pages/host/PostJobPage"));
const HostPaymentsPage = lazy(() => import("./pages/host/WalletPage"));
const JobFeed = lazy(() => import("./pages/cleaner/JobFeed"));
const MyJobsPage = lazy(() => import("./pages/cleaner/MyJobsPage"));
const KYCSubmissionPage = lazy(
  () => import("./pages/cleaner/KYCSubmissionPage"),
);
const EarningsPage = lazy(() => import("./pages/cleaner/EarningsPage"));
const AgentDashboard = lazy(() => import("./pages/agent/AgentDashboard"));
const ReferralHistoryPage = lazy(
  () => import("./pages/agent/ReferralHistoryPage"),
);
const SettingsPage = lazy(() => import("./pages/shared/SettingsPage"));
const NotFoundPage = lazy(() => import("./pages/shared/NotFoundPage"));

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true">
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  );
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const map: Record<string, string> = {
    admin: "/admin",
    host: "/host",
    cleaner: "/cleaner",
    field_agent: "/agent",
  };
  return <Navigate to={map[user.role] ?? "/login"} replace />;
}

export function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/:role" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/setup-account" element={<SetupAccountPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/faq" element={<Navigate to="/help" replace />} />
        <Route path="/hosts" element={<HostsPage />} />
        <Route path="/cleaners" element={<CleanersPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/kyc-policy" element={<KycPolicyPage />} />
        <Route path="/safety" element={<SafetyPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="kyc" element={<KYCReview />} />
          <Route path="config" element={<SystemConfig />} />
          <Route path="disputes" element={<DisputeResolution />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="agents" element={<AdminAgents />} />
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="properties" element={<AdminProperties />} />
        </Route>
        <Route
          path="/host"
          element={
            <ProtectedRoute roles={["host"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<HostDashboard />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/new" element={<PropertyFormPage />} />
          <Route path="properties/:id/edit" element={<PropertyFormPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/new" element={<PostJobPage />} />
          <Route path="payments" element={<HostPaymentsPage />} />
          <Route path="wallet" element={<Navigate to="/host/payments" replace />} />
        </Route>
        <Route
          path="/cleaner"
          element={
            <ProtectedRoute roles={["cleaner"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<JobFeed />} />
          <Route path="my-jobs" element={<MyJobsPage />} />
          <Route path="kyc" element={<KYCSubmissionPage />} />
          <Route path="earnings" element={<EarningsPage />} />
        </Route>
        <Route
          path="/agent"
          element={
            <ProtectedRoute roles={["field_agent"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<AgentDashboard />} />
          <Route path="referrals" element={<ReferralHistoryPage />} />
        </Route>
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
